<template>
  <!-- Mobile menu overlay -->
  <div class="mobile-menu-overlay" v-if="hamburgerMenuActive" @click="toggleHamburgerMenu()"></div>
  
  <!-- Mobile navigation menu -->
  <div class="mobile-menu" :class="{ active: hamburgerMenuActive }">
    <div class="mobile-menu-header">
      <div class="mobile-logo">
        <router-link :to="{ name: 'home' }">
          <img loading="lazy" src="/img/logo.svg" alt="CrowdHrs" title="CrowdHrs" />
        </router-link>
      </div>
      <button class="close-menu-btn" @click="toggleHamburgerMenu()">
        <i class="pi pi-times"></i>
      </button>
    </div>
    
    <nav class="mobile-nav">
      <ul class="mobile-nav-list">
        <li class="mobile-nav-item">
          <router-link :to="{ name: 'home' }" class="mobile-nav-link">
            <i class="pi pi-home"></i>
            <span>Home</span>
          </router-link>
        </li>
        <li class="mobile-nav-item" v-if="account.loggeduser">
          <router-link :to="{ name: 'jobOpenings' }" class="mobile-nav-link">
            <i class="pi pi-th-large"></i>
            <span>My Dashboard</span>
          </router-link>
        </li>
        <li class="mobile-nav-item">
          <router-link :to="{ name: 'contactUs' }" class="mobile-nav-link">
            <i class="pi pi-envelope"></i>
            <span>Contact Us</span>
          </router-link>
        </li>
        <li class="mobile-nav-item" v-if="!account.loggeduser">
          <router-link :to="{ name: 'login' }" class="mobile-nav-link">
            <i class="pi pi-user"></i>
            <span>Login</span>
          </router-link>
        </li>
        <li class="mobile-nav-item" v-if="!account.loggeduser">
          <router-link :to="{ name: 'getStarted' }" class="text-decoration-none w-100">
            <CButton
              class="w-100"
              variant="solid"
              color="primary"
              label="Get Started"
              icon="pi pi-arrow-right"
              iconPos="right"
            />
          </router-link>
        </li>
      </ul>
    </nav>
  </div>
  
  <!-- Desktop topbar -->
  <div class="topbar-container container mx-auto" :class="{ 'scrolled': scrolled }">
    <div class="topbar">
      <div class="logo-container">
        <router-link :to="{ name: 'home' }">
          <img
            loading="lazy"
            src="/img/logo.svg"
            alt="CrowdHrs"
            title="CrowdHrs"
            class="logo"
          />
        </router-link>
      </div>
      
      <nav class="desktop-nav">
        <!-- Hamburger menu button for mobile -->
        <button
          class="hamburger-btn"
          @click="toggleHamburgerMenu()"
        >
          <span class="bar"></span>
          <span class="bar"></span>
          <span class="bar"></span>
        </button>
        
        <!-- Desktop navigation -->
        <ul class="nav-list">
          <li class="nav-item">
            <router-link :to="{ name: 'home' }" class="nav-link">Home</router-link>
          </li>
          <li class="nav-item" v-if="account.loggeduser">
            <router-link :to="{ name: 'jobOpenings' }" class="nav-link">My Dashboard</router-link>
          </li>
          <li class="nav-item">
            <router-link :to="{ name: 'contactUs' }" class="nav-link">Contact Us</router-link>
          </li>
          
          <!-- Login button for non-logged users -->
          <li class="nav-item" v-if="!account.loggeduser">
            <router-link :to="{ name: 'login' }" class="nav-link login-link">
              <i class="pi pi-user"></i>
              <span>Login</span>
            </router-link>
          </li>
          
          <!-- Get Started button for non-logged users -->
          <li class="nav-item" v-if="!account.loggeduser">
            <router-link :to="{ name: 'getStarted' }" class="text-decoration-none">
              <CButton
                variant="solid"
                color="primary"
                label="Get Started"
                icon="pi pi-arrow-right"
                iconPos="right"
              />
            </router-link>
          </li>
          
          <!-- User profile dropdown for logged users -->
          <li class="nav-item user-profile" v-if="account.loggeduser">
            <div
              class="profile-menu"
              @click="toggleMenu($event)"
              :class="{ 'active': menuOpen }"
            >
              <div class="profile-trigger">
                <div class="avatar-container">
                  <Avatar
                    v-if="!profileData.profile_picture_path"
                    :label="getInitials"
                    class="avatar"
                    size="normal"
                    shape="circle"
                  />
                  <Avatar
                    v-else
                    :image="logoURL + profileData.profile_picture_path"
                    class="avatar"
                    size="normal"
                    shape="circle"
                  />
                </div>
                
                <div class="profile-info">
                  <span class="profile-name">
                    {{ profileData.firstname }} {{ profileData.lastname }}
                  </span>
                  <div class="role-points-container">
                    <span class="profile-role">
                      {{ profileData.role_name }}
                    </span>
                    <div v-if="shouldShowPoints" class="points-badge">
                      <i :class="`pi pi-${profileData.points > 0 ? 'star-fill' : 'star'}`" class="points-icon"></i>
                      <span class="points-value">{{ profileData.points }} Points</span>
                    </div>
                  </div>
                </div>
                
                <i class="pi pi-chevron-down dropdown-icon"></i>
              </div>
            </div>
            
            <!-- Profile dropdown menu -->
            <Menu ref="menu" id="profile_menu" :model="menuItems" :popup="true" class="profile-dropdown">
              <template #item="{ item }">
                <button
                  v-if="!item.route"
                  type="button"
                  class="menu-item"
                  :class="{ 'danger-item': item.label === 'Logout' }"
                  @click="item.command"
                >
                  <span class="menu-item-icon">
                    <i :class="item.icon"></i>
                  </span>
                  <span class="menu-item-label">{{ item.label }}</span>
                </button>
                <router-link
                  v-else
                  :to="{ name: item.route }"
                  class="menu-item"
                >
                  <span class="menu-item-icon">
                    <i :class="item.icon"></i>
                  </span>
                  <span class="menu-item-label">{{ item.label }}</span>
                </router-link>
              </template>
            </Menu>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { post, logoURL } from '@/api/Services'
import Avatar from 'primevue/avatar'

export default {
  emits: ['gotoHome-click', 'getStarted-click', 'login-click'],

  components: {
    Avatar,
  },

  data() {
    return {
      logoURL: logoURL,
      showMenu: false,
      username: 'Kullanıcı Adı',
      hamburgerMenuActive: false,
      avatar: false,
      menuOpen: false,
      scrolled: false,
      menuItems: [
        { label: 'User Profile', icon: 'pi pi-user', route: 'profile' },
        {
          label: 'Change Password',
          icon: 'pi pi-lock',
          route: 'changePassword',
        },
        {
          label: 'Help',
          icon: 'pi pi-question',
          command: () => {
            this.$router.push({ name: 'faq' })
          },
        },
        {
          label: 'Logout',
          icon: 'pi pi-sign-out',
          command: this.logout,
        },
      ],
    }
  },
  async mounted() {
    await this.getProfile()
    
    // Close menu when clicking outside
    document.addEventListener('click', this.handleOutsideClick);
    
    // Add scroll event listener
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('scroll', this.handleScroll);
  },
  props: {
    horizontal: {
      type: Boolean,
      default: false,
    },
    rightBar: {
      type: Boolean,
      default: true,
    },
    topbarMenuActive: {
      type: Boolean,
      default: false,
    },
    activeTopbarItem: String,
    mobileTopbarActive: Boolean,
    searchActive: Boolean,
  },
  methods: {
    toggleHamburgerMenu() {
      this.hamburgerMenuActive = !this.hamburgerMenuActive;
      
      // Disable body scrolling when menu is open
      if (this.hamburgerMenuActive) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    handleOutsideClick(event) {
      if (!this.$el || typeof this.$el.querySelector !== 'function') {
        return;
      }
      
      const profileMenu = this.$el.querySelector('.profile-menu');
      const menu = this.$refs.menu?.$el;
      
      if (profileMenu && menu && this.menuOpen) {
        if (!profileMenu.contains(event.target) && !menu.contains(event.target)) {
          this.menuOpen = false;
        }
      }
    },
    handleScroll() {
      this.scrolled = window.scrollY > 10;
    },
    async getProfile() {
      const parameters = [
        {
          parameter_name: 'pin_selected_user_id',
          parameter_value: this.account.loggeduser?.id || -1,
        },
      ]

      try {
        const data = await post('Commons/Executer', {
          sqlId: 25,
          parameters: JSON.stringify(parameters),
        })
        if (data) {
          this.userParam = data[0]
        }
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to fetch profile.',
          life: 3000,
        })
      } finally {
        this.loading = false
      }
    },
    toggleMenu(event) {
      event.stopPropagation();
      this.menuOpen = !this.menuOpen;
      this.$refs.menu.toggle(event);
    },
    isMobile() {
      return window.innerWidth <= 1024
    },
    logout() {
      this.$store.dispatch('account/logout')
        .then(() => {
          this.$router.push({ name: 'login' });
        })
        .catch(error => {
          console.error('Error during logout:', error);
          this.$router.push({ name: 'login' });
        });
    },
  },
  computed: {
    getInitials() {
      if (
        !this.profileData ||
        !this.profileData.firstname ||
        !this.profileData.lastname
      ) {
        return '';
      }

      return (
        (this.profileData.firstname[0]?.toUpperCase() || '') +
        (this.profileData.lastname[0]?.toUpperCase() || '')
      );
    },
    shouldShowPoints() {
      const userParam = this.$store.state.account.userParam;
      return (
        userParam &&
        userParam.role_id === 2 &&
        userParam.points !== undefined
      );
    },
    profileData() {
      const userParam = this.$store.state.account.userParam;
      
      // If userParam is null or undefined, return empty values
      if (!userParam) {
        return {
          firstname: '',
          lastname: '',
          role_name: '',
          profile_picture_path: '',
          points: 0,
        };
      }

      return {
        firstname: userParam.firstname || '',
        lastname: userParam.lastname || '',
        role_name: userParam.role_name || '',
        profile_picture_path: userParam.profile_picture_path || '',
        points: userParam.points || 0,
      };
    },
    ...mapState({
      account: (state) => state.account,
      userParam: (state) => state.account.userParam,
    }),
    ...mapActions('account', ['logout']),
  },
}
</script>

<style lang="scss" scoped>
// Variables
$primary-color: #015cac;
$accent-color: #fc6c2a;
$text-color: #333;
$light-gray: #f8f9fa;
$medium-gray: #e9ecef;
$dark-gray: #6c757d;
$transition-fast: 0.2s ease;
$transition-normal: 0.3s ease;
$box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

// Main container
.topbar-container {
  padding: 0 1.5rem;
  background-color: #fff;
  box-shadow: none;
  position: sticky;
  margin: 0 auto;
  top: 0;
  z-index: 999;
  transition: all 0.3s ease;
  border-radius: 0 0 16px 16px;
  
  &.scrolled {
    box-shadow: $box-shadow;
  }
}

.topbar {
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
}

// Logo
.logo-container {
  display: flex;
  align-items: center;
  
  img.logo {
    max-height: 60px;
    width: auto;
  }
}

// Desktop Navigation
.desktop-nav {
  display: flex;
  align-items: center;
}

.nav-list {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 1.5rem;
  
  @media (max-width: 1024px) {
    display: none;
  }
}

.nav-item {
  position: relative;
}

.nav-link {
  color: $text-color;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  transition: all $transition-fast;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    color: $primary-color;
    background-color: rgba($primary-color, 0.05);
  }
  
  &.router-link-active {
    color: $primary-color;
    font-weight: 600;
  }
}

.login-link {
  i {
    font-size: 0.9rem;
  }
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: $accent-color;
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all $transition-fast;
  
  i {
    transition: transform $transition-normal;
  }
  
  &:hover {
    
    i {
      transform: translateX(3px);
    }
  }
}

// Hamburger button
.hamburger-btn {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  
  @media (max-width: 1024px) {
    display: flex;
  }
  
  .bar {
    width: 100%;
    height: 3px;
    background-color: $text-color;
    border-radius: 3px;
    transition: all $transition-normal;
  }
}

// Mobile menu
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  backdrop-filter: blur(3px);
  opacity: 0;
  animation: fadeIn 0.3s forwards;
}

@keyframes fadeIn {
  to { opacity: 1; }
}

.mobile-menu {
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 360px;
  height: 100%;
  background-color: white;
  z-index: 9999;
  padding: 2rem;
  transform: translateX(100%);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
  box-shadow: -5px 0 25px rgba(0, 0, 0, 0.15);
  
  &.active {
    transform: translateX(0);
  }
}

.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  .mobile-logo img {
    max-height: 36px;
  }
}

.close-menu-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color $transition-fast;
  
  i {
    font-size: 1.5rem;
    color: $text-color;
  }
  
  &:hover {
    background-color: $light-gray;
  }
}

.mobile-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.mobile-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-nav-item {
  width: 100%;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  color: $text-color;
  text-decoration: none;
  font-weight: 500;
  transition: background-color $transition-fast;
  
  i {
    font-size: 1.25rem;
    color: $primary-color;
    width: 24px;
    text-align: center;
  }
  
  &:hover {
    background-color: $light-gray;
  }
  
  &.router-link-active {
    background-color: rgba($primary-color, 0.1);
    color: $primary-color;
    font-weight: 600;
  }
}

.mobile-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background-color: $accent-color;
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all $transition-fast;
}

// User profile section
.user-profile {
  position: relative;
}

.profile-menu {
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.active {
    .profile-trigger {
      background-color: rgba($primary-color, 0.08);
    }
  }
}

.profile-trigger {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  gap: 0.75rem;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba($primary-color, 0.05);
  }
}

.avatar-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar {
  width: 40px;
  height: 40px;
  
  ::v-deep(.p-avatar) {
    background-color: $primary-color;
    color: white;
    font-weight: 600;
  }
}

.profile-info {
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1200px) {
    display: none;
  }
}

.profile-name {
  color: $text-color;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.2;
}

.profile-role {
  color: $dark-gray;
  font-size: 0.8rem;
  line-height: 1.2;
}

.points-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: rgba(255, 215, 0, 0.15);
  border-radius: 10px;
  padding: 2px 6px;
  max-width: fit-content;
  border: 1px solid rgba(255, 215, 0, 0.2);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 215, 0, 0.2);
  }
}

.points-icon {
  color: #ffd700;
  font-size: 0.75rem;
}

.points-value {
  font-weight: 500;
  color: $text-color;
  font-size: 0.7rem;
  margin-left: 3px;
}

.dropdown-icon {
  font-size: 0.8rem;
  color: $dark-gray;
  transition: transform 0.2s ease;
  
  &.active {
    transform: rotate(180deg);
  }
  
  @media (max-width: 1200px) {
    display: none;
  }
}

// Profile dropdown
::v-deep(.p-menu.profile-dropdown) {
  border: none;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  padding: 0.5rem;
  min-width: 220px;
  
  .p-menuitem {
    margin-bottom: 0.25rem;
  }
  
  .p-menu-list {
    padding: 0;
  }
}

.menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  text-decoration: none;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba($primary-color, 0.08);
  }
  
  &.danger-item {
    color: #dc3545;
    
    &:hover {
      background-color: rgba(#dc3545, 0.08);
    }
    
    .menu-item-icon i {
      color: #dc3545;
    }
  }
}

.menu-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  
  i {
    font-size: 1rem;
    color: $primary-color;
  }
}

.menu-item-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: $text-color;
}

.role-points-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}
</style>

